/* eslint-disable */

/** The outcome of attempting to link a credit card or bank account. */
export enum LinkResult {
  SUCCESS = "SUCCESS",
  /**
   * ERROR - A generic error code used to denote when normal link result processing is
   * interrupted by an unrecoverable error.
   */
  ERROR = "ERROR",
  BANK_INVALID_ACCOUNT_NUMBER = "BANK_INVALID_ACCOUNT_NUMBER",
  BANK_INVALID_ROUTING_NUMBER = "BANK_INVALID_ROUTING_NUMBER",
  /**
   * BANK_NOT_FOUND_ROUTING_NUMBER - The routing number is structurally valid, but we could not find it in our internal list
   * of existing routing numbers.
   */
  BANK_NOT_FOUND_ROUTING_NUMBER = "BANK_NOT_FOUND_ROUTING_NUMBER",
  BANK_PREVIOUSLY_FAILED_PUSH = "BANK_PREVIOUSLY_FAILED_PUSH",
  /**
   * BANK_EXPECTED_ROUTING_GOT_WIRE_TRANSFER_NUMBER - We expected a regular routing number but we received a wire transfer number. These numbers
   * fail the vast majority of ACH pushes and the customers should instead enter their regular
   * routing number.
   */
  BANK_EXPECTED_ROUTING_GOT_WIRE_TRANSFER_NUMBER = "BANK_EXPECTED_ROUTING_GOT_WIRE_TRANSFER_NUMBER",
  BANK_MISSING_ACCOUNT_NUMBER = "BANK_MISSING_ACCOUNT_NUMBER",
  BANK_MISSING_ROUTING_NUMBER = "BANK_MISSING_ROUTING_NUMBER",
  CARD_BRAND_UNSUPPORTED = "CARD_BRAND_UNSUPPORTED",
  CARD_LENGTH_INVALID = "CARD_LENGTH_INVALID",
  CARD_LINKED_TOO_MANY_ACCOUNTS = "CARD_LINKED_TOO_MANY_ACCOUNTS",
  CARD_NUMBER_INVALID = "CARD_NUMBER_INVALID",
  CARD_NUMBER_MISSING = "CARD_NUMBER_MISSING",
  CARD_NUMBER_TOO_LONG = "CARD_NUMBER_TOO_LONG",
  CARD_NUMBER_TOO_SHORT = "CARD_NUMBER_TOO_SHORT",
  CARD_VERIFICATION_ERROR = "CARD_VERIFICATION_ERROR",
  EXPIRED = "EXPIRED",
  INVALID_BIN = "INVALID_BIN",
  PREPAID_BIN = "PREPAID_BIN",
  INVALID_COUNTRY = "INVALID_COUNTRY",
  NO_CARD_TYPE = "NO_CARD_TYPE",
  NO_INFORMATION_FOR_BIN = "NO_INFORMATION_FOR_BIN",
  INVALID_OR_NO_PREPAID_STATUS = "INVALID_OR_NO_PREPAID_STATUS",
  INVALID_OR_NO_CARD_PRODUCT = "INVALID_OR_NO_CARD_PRODUCT",
  INVALID_BIN_US_DEBIT_OR_CREDIT = "INVALID_BIN_US_DEBIT_OR_CREDIT",
  INVALID_EXPIRATION = "INVALID_EXPIRATION",
  INVALID_ZIP = "INVALID_ZIP",
  NEED_BANK_ACCOUNT = "NEED_BANK_ACCOUNT",
  /**
   * SENDER_NON_MTL - Sender's ZIP codes must be in a region that we're licensed to send money
   * from. At launch this was 48 of 50 United States. We cannot send from Puerto
   * Rico or other regions that use US ZIP codes.
   */
  SENDER_NON_MTL = "SENDER_NON_MTL",
  TOO_MANY_FAILED_CARD_LINK_ATTEMPTS = "TOO_MANY_FAILED_CARD_LINK_ATTEMPTS",
  VISA_PREPAID_CARD = "VISA_PREPAID_CARD",
  INVALID_CVV = "INVALID_CVV",
  BANK_LINKED_TOO_MANY_ACCOUNTS = "BANK_LINKED_TOO_MANY_ACCOUNTS",
  TOO_MANY_FAILED_CVV_ATTEMPTS = "TOO_MANY_FAILED_CVV_ATTEMPTS",
  CARD_BLOCKED_BY_TOTAL_FAILED_CVV_ATTEMPTS = "CARD_BLOCKED_BY_TOTAL_FAILED_CVV_ATTEMPTS",
  /**
   * RECIPIENT_NON_MTL - Recipient's ZIP codes must be in a state that we're licensed to receive
   * money to. At launch this was all 50 United States. We cannot receive in
   * Puerto Rico or other regions that use US ZIP codes.
   */
  RECIPIENT_NON_MTL = "RECIPIENT_NON_MTL",
  /** AUTO_UPDATED - The card link was generated from an Account Updater (Panfresh) update */
  AUTO_UPDATED = "AUTO_UPDATED",
  INVALID_CVV_OR_EXPIRATION = "INVALID_CVV_OR_EXPIRATION",
  CARD_BLACKLISTED = "CARD_BLACKLISTED",
  CARD_DENYLISTED = "CARD_DENYLISTED",
  BANK_ACCOUNT_BLACKLISTED = "BANK_ACCOUNT_BLACKLISTED",
  BANK_ACCOUNT_DENYLISTED = "BANK_ACCOUNT_DENYLISTED",
  /**
   * NORMALIZED_BANK_ACCOUNT_BLACKLISTED - Fraudsters use an ACH trick to keep receiving money by changing routing numbers. To cope with
   * that, we denylist a normalized bank account fidelius token. This has a tiny chance of false
   * positives (in the order of 1 in 10 million for 9 digit account numbers).
   */
  NORMALIZED_BANK_ACCOUNT_BLACKLISTED = "NORMALIZED_BANK_ACCOUNT_BLACKLISTED",
  NORMALIZED_BANK_ACCOUNT_DENYLISTED = "NORMALIZED_BANK_ACCOUNT_DENYLISTED",
  ROUTING_NUMBER_BLACKLISTED = "ROUTING_NUMBER_BLACKLISTED",
  ROUTING_NUMBER_DENYLISTED = "ROUTING_NUMBER_DENYLISTED",
  /** CARD_EXPECTED_DEBIT_GOT_CREDIT - The request was for a debit card but the user provided a credit card. */
  CARD_EXPECTED_DEBIT_GOT_CREDIT = "CARD_EXPECTED_DEBIT_GOT_CREDIT",
  /** CARD_EXPECTED_CREDIT_GOT_DEBIT - The request was for a credit card but the user provided a debit card. */
  CARD_EXPECTED_CREDIT_GOT_DEBIT = "CARD_EXPECTED_CREDIT_GOT_DEBIT",
  /** CARD_UNSUPPORTED_UNLINKED_ON_PUSH_OR_PULL - The card is unsupported and was unlinked during a push or pull attempt. */
  CARD_UNSUPPORTED_UNLINKED_ON_PUSH_OR_PULL = "CARD_UNSUPPORTED_UNLINKED_ON_PUSH_OR_PULL",
  /** AMEX_LINKED_BY_INELIGIBLE_CUSTOMER - The customer did not qualify to link an Amex card at the time of the link. */
  AMEX_LINKED_BY_INELIGIBLE_CUSTOMER = "AMEX_LINKED_BY_INELIGIBLE_CUSTOMER",
  /** CREDIT_CARD_LINKED_BY_INELIGIBLE_CUSTOMER - The customer did not qualify to link a credit card at the time of the link. */
  CREDIT_CARD_LINKED_BY_INELIGIBLE_CUSTOMER = "CREDIT_CARD_LINKED_BY_INELIGIBLE_CUSTOMER",
  /** CARD_LINK_BLOCKED_BY_RISK - The customer's card link was flagged by risk. */
  CARD_LINK_BLOCKED_BY_RISK = "CARD_LINK_BLOCKED_BY_RISK",
  /** BANK_ACCOUNT_PERMANENTLY_FAILED - Reasons from Gringotts to consider linking a bank account less than successful. */
  BANK_ACCOUNT_PERMANENTLY_FAILED = "BANK_ACCOUNT_PERMANENTLY_FAILED",
  BANK_ACCOUNT_NOT_VERIFIED = "BANK_ACCOUNT_NOT_VERIFIED",
  BANK_ACCOUNT_BLACKLISTED_IN_GRINGOTTS = "BANK_ACCOUNT_BLACKLISTED_IN_GRINGOTTS",
  BANK_ACCOUNT_DENYLISTED_IN_GRINGOTTS = "BANK_ACCOUNT_DENYLISTED_IN_GRINGOTTS",
  BANK_ACCOUNT_UNEXPECTED_GRINGOTTS_BLOCKER = "BANK_ACCOUNT_UNEXPECTED_GRINGOTTS_BLOCKER",
  /** SQUARE_ISSUED_CARD - We do not allow linking our own issued card in Cash App. */
  SQUARE_ISSUED_CARD = "SQUARE_ISSUED_CARD",
  /** INSTRUMENT_LINKING_DISABLED - We have an experiment where instrument linking can be disabled * */
  INSTRUMENT_LINKING_DISABLED = "INSTRUMENT_LINKING_DISABLED",
  /** CARD_LINK_BLOCKED_BY_CUSTOMER_DENYLIST_RULES - Customer failed to link instrument (bank account/card) due to customer denylist link rules */
  CARD_LINK_BLOCKED_BY_CUSTOMER_DENYLIST_RULES = "CARD_LINK_BLOCKED_BY_CUSTOMER_DENYLIST_RULES",
  /**
   * THREEDS_CHALLENGE_REQUIRED - Some cards require the completion of a 3DS challenge flow with their issuing bank before we
   * can link their card. This is currently only used as part of the PSD2 SCA mandate in the EU.
   */
  THREEDS_CHALLENGE_REQUIRED = "THREEDS_CHALLENGE_REQUIRED",
  THREEDS_CHALLENGE_FAILED = "THREEDS_CHALLENGE_FAILED",
  THREEDS_SERVICE_FAILURE = "THREEDS_SERVICE_FAILURE",
  /** BANK_LINK_BLOCKED_BY_CUSTOMER_DENYLIST_RULES - Customer failed to link bank account due to customer denylist link rules */
  BANK_LINK_BLOCKED_BY_CUSTOMER_DENYLIST_RULES = "BANK_LINK_BLOCKED_BY_CUSTOMER_DENYLIST_RULES",
  /** INSTRUMENT_LINK_BLOCKED_BY_COMPLIANCE_HIGH_RISK - Customer is blocked by compliance checks */
  INSTRUMENT_LINK_BLOCKED_BY_COMPLIANCE_HIGH_RISK = "INSTRUMENT_LINK_BLOCKED_BY_COMPLIANCE_HIGH_RISK",
}
